import { useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { Employee, Timesheet } from '@/types/timesheet';

interface UseApprovalFiltersParams {
  pendingTimesheets: Timesheet[];
  employees: Employee[];
  timesheetAnomalies: Map<number, AnomalyDetectionResult>;
  timesheetPolicyViolations: Map<number, PolicyViolation[]>;
}

export interface ApprovalSummary {
  pending: number;
  overdue: number;
  totalHours: number;
  withAnomalies: number;
  highSeverity: number;
  mediumSeverity: number;
  lowSeverity: number;
}

export interface UseApprovalFiltersReturn {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  departmentFilter: string;
  setDepartmentFilter: Dispatch<SetStateAction<string>>;
  anomalyFilter: string;
  setAnomalyFilter: Dispatch<SetStateAction<string>>;
  overdueFilter: boolean;
  setOverdueFilter: Dispatch<SetStateAction<boolean>>;
  filteredTimesheets: Timesheet[];
  departments: string[];
  summary: ApprovalSummary;
}

/**
 * Owns the approval queue filter state and the derived filtered list,
 * department options and KPI summary.
 */
export function useApprovalFilters({
  pendingTimesheets,
  employees,
  timesheetAnomalies,
  timesheetPolicyViolations,
}: UseApprovalFiltersParams): UseApprovalFiltersReturn {
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [anomalyFilter, setAnomalyFilter] = useState<string>('all');
  const [overdueFilter, setOverdueFilter] = useState<boolean>(false);

  // Apply filters
  const filteredTimesheets = useMemo(() => {
    const filtered = pendingTimesheets.filter(ts => {
      const employee = employees.find(e => e.id === ts.employeeId);
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];

      const matchesSearch = searchTerm === '' ||
        ts.employeeName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDepartment = departmentFilter === 'all' ||
        employee?.department === departmentFilter;

      // Check if timesheet has any issues (anomalies or policy violations)
      const hasIssues = anomalyResult?.hasAnomalies || policyViolations.length > 0;
      const matchesAnomaly = anomalyFilter === 'all' ||
        (anomalyFilter === 'with-anomalies' && hasIssues) ||
        (anomalyFilter === 'clean' && !hasIssues);

      // Check overdue filter (>7 days = overdue per PRD F-05)
      const daysSinceSubmission = ts.submittedAt
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      const isOverdue = daysSinceSubmission > 7;
      const matchesOverdue = !overdueFilter || isOverdue;

      const passes = matchesSearch && matchesDepartment && matchesAnomaly && matchesOverdue;

      return passes;
    });

    return filtered;
  }, [pendingTimesheets, searchTerm, departmentFilter, anomalyFilter, overdueFilter, employees, timesheetAnomalies, timesheetPolicyViolations]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const pending = pendingTimesheets.length;
    const overdue = pendingTimesheets.filter(ts => {
      const daysSinceSubmission = ts.submittedAt
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      return daysSinceSubmission > 7;
    }).length;

    const totalHours = pendingTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
      }, 0);
    }, 0);

    // Calculate anomaly and policy violation stats
    let withAnomalies = 0;
    let highSeverity = 0;
    let mediumSeverity = 0;
    let lowSeverity = 0;

    pendingTimesheets.forEach(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];

      // Check for anomalies or policy violations
      const hasAnomalies = anomalyResult?.hasAnomalies;
      const hasPolicyViolations = policyViolations.length > 0;

      if (hasAnomalies || hasPolicyViolations) {
        withAnomalies++;

        // Check anomaly severities
        const hasHighAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'high') ?? false;
        const hasMediumAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'medium') ?? false;
        const hasLowAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'low') ?? false;

        // Check policy violation severities
        const hasHighViolation = policyViolations.some(v => v.severity === 'high');
        const hasMediumViolation = policyViolations.some(v => v.severity === 'medium');
        const hasLowViolation = policyViolations.some(v => v.severity === 'low');

        // Combine both sources
        if (hasHighAnomaly || hasHighViolation) highSeverity++;
        else if (hasMediumAnomaly || hasMediumViolation) mediumSeverity++;
        else if (hasLowAnomaly || hasLowViolation) lowSeverity++;
      }
    });

    return { pending, overdue, totalHours, withAnomalies, highSeverity, mediumSeverity, lowSeverity };
  }, [pendingTimesheets, timesheetAnomalies, timesheetPolicyViolations]);

  const departments = useMemo(() => {
    const depts = new Set(employees.map(e => e.department).filter(Boolean));
    return Array.from(depts);
  }, [employees]);

  return {
    searchTerm,
    setSearchTerm,
    departmentFilter,
    setDepartmentFilter,
    anomalyFilter,
    setAnomalyFilter,
    overdueFilter,
    setOverdueFilter,
    filteredTimesheets,
    departments,
    summary,
  };
}
